import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Mail, Building, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company_name: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', company_name: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section data-testid="contact-hero" className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1772631653221-9c77e6f82c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBkYXJrJTIwbmlnaHR8ZW58MHx8fHwxNzc2NTM0MDU0fDA&ixlib=rb-4.1.0&q=85)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C1C] via-[#0A0C1C]/95 to-[#12142B]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Info */}
            <motion.div initial="hidden" animate="visible">
              <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
                Contact Us
              </motion.p>
              <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.1]">
                Let's Build Something{' '}
                <span className="bg-gradient-to-r from-[#E65A14] to-[#FFBE50] bg-clip-text text-transparent">
                  Powerful
                </span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="mt-6 text-base sm:text-lg text-[#C8C8C8] leading-relaxed">
                Ready to take your business to the next level with smarter systems? Get in touch with us today.
              </motion.p>

              <motion.div variants={fadeUp} custom={3} className="mt-12 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#E65A14]/15 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-[#FF8C28]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8A8D9F] mb-0.5">Email</p>
                    <p className="text-sm text-white">info@embercoredigital.co.za</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#E65A14]/15 flex items-center justify-center flex-shrink-0">
                    <Building size={20} className="text-[#FF8C28]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8A8D9F] mb-0.5">Website</p>
                    <p className="text-sm text-white">embercoredigital.co.za</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#E65A14]/15 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#FF8C28]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8A8D9F] mb-0.5">Location</p>
                    <p className="text-sm text-white">South Africa</p>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} custom={4} className="mt-12 p-6 rounded-xl bg-[#12142B]/80 border border-[#FF8C28]/15">
                <h3 className="font-heading text-lg font-bold text-white mb-2">
                  Book a Free 30-Minute Consultation
                </h3>
                <p className="text-sm text-[#C8C8C8] leading-relaxed">
                  Let's explore how we can streamline your business and build something that scales.
                </p>
              </motion.div>
            </motion.div>

            {/* Right: Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#E65A14]/20 to-[#FF8C28]/10 rounded-2xl blur-xl" />
                <div className="relative bg-[#12142B] border border-[#FF8C28]/20 rounded-xl p-8">
                  {status === 'success' ? (
                    <div data-testid="contact-success-message" className="text-center py-12">
                      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-[#E65A14] to-[#FF8C28] flex items-center justify-center mb-6">
                        <CheckCircle size={32} className="text-white" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-white mb-3">Message Sent!</h3>
                      <p className="text-[#C8C8C8]">We'll get back to you within 24 hours.</p>
                      <button
                        data-testid="contact-send-another-btn"
                        onClick={() => setStatus('idle')}
                        className="mt-6 text-sm text-[#FF8C28] hover:text-[#FFBE50] transition-colors"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                      <h3 className="font-heading text-xl font-bold text-white mb-2">Send us a message</h3>

                      <div>
                        <Label htmlFor="name" className="text-sm text-[#C8C8C8] mb-1.5 block">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          data-testid="contact-name-input"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="bg-[#0A0C1C] border-[#FF8C28]/15 text-white placeholder:text-[#8A8D9F] focus:border-[#FF8C28]/50 focus:ring-[#E65A14]/20"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-sm text-[#C8C8C8] mb-1.5 block">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="contact-email-input"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="you@company.com"
                          className="bg-[#0A0C1C] border-[#FF8C28]/15 text-white placeholder:text-[#8A8D9F] focus:border-[#FF8C28]/50 focus:ring-[#E65A14]/20"
                        />
                      </div>

                      <div>
                        <Label htmlFor="company_name" className="text-sm text-[#C8C8C8] mb-1.5 block">Company Name</Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          data-testid="contact-company-input"
                          value={form.company_name}
                          onChange={handleChange}
                          placeholder="Your company"
                          className="bg-[#0A0C1C] border-[#FF8C28]/15 text-white placeholder:text-[#8A8D9F] focus:border-[#FF8C28]/50 focus:ring-[#E65A14]/20"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-sm text-[#C8C8C8] mb-1.5 block">What do you need help with? *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          data-testid="contact-message-input"
                          value={form.message}
                          onChange={handleChange}
                          required
                          rows={4}
                          placeholder="Tell us about your project..."
                          className="bg-[#0A0C1C] border-[#FF8C28]/15 text-white placeholder:text-[#8A8D9F] focus:border-[#FF8C28]/50 focus:ring-[#E65A14]/20 resize-none"
                        />
                      </div>

                      {status === 'error' && (
                        <p data-testid="contact-error-message" className="text-sm text-red-400">{errorMsg}</p>
                      )}

                      <button
                        type="submit"
                        data-testid="contact-submit-button"
                        disabled={status === 'loading'}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_30px_rgba(230,90,20,0.5)] disabled:opacity-50 transition-all duration-300"
                      >
                        {status === 'loading' ? (
                          <span className="animate-pulse">Sending...</span>
                        ) : (
                          <>
                            <Send size={16} />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
